import React from "react"
import { Toggle } from "../Toggle"
import { SIGNUP_URL } from "../../constants/urls"

// Non Typed External Libs
const Cookies = require("js-cookie")

interface PricingCalculatorProps {
  numberOfPeople: number,
  defaultItemsPerOrder: number,
  plans: Plan[],
}

interface PricingCalculatorState {
  itemsPerOrder: number,
  plan: Plan,
}

interface Plan {
  id: number,
  planOptions: PlanOption[],
}

interface PlanOption {
  itemsPerOrderOptions: number[],
  tiers: PricingTier[],
}

interface PricingTier {
  min: number,
  max: number,
  pricePerUnit: number,
}

class PricingCalculatorInner extends
  React.Component<PricingCalculatorProps, PricingCalculatorState> {
  static defaultProps: PricingCalculatorProps = {
    numberOfPeople: 2,
    defaultItemsPerOrder: 3,
    plans: [{
      id: 38,
      planOptions: [{
        itemsPerOrderOptions: [3, 4, 5],
        tiers: [{ min: 0, max: 999, pricePerUnit: 11.99 }],
      }],
    }],
  }

  constructor(props: PricingCalculatorProps) {
    super(props)
    const { defaultItemsPerOrder } = this.props

    this.state = {
      itemsPerOrder: defaultItemsPerOrder,
      plan: this.findPlan(),
    }

    this.renderPerWeek = this.renderPerWeek.bind(this)
    this.renderPerServing = this.renderPerServing.bind(this)
    this.selectItemsPerOrder = this.selectItemsPerOrder.bind(this)
    this.formatTotal = this.formatTotal.bind(this)
    this.ariaSubmitLabel = this.ariaSubmitLabel.bind(this)
    this.renderCTA = this.renderCTA.bind(this)
  }

  findPlan(): Plan {
    const { plans } = this.props
    const planId = parseInt(Cookies.get("plan_id"), 10)
    const plan = plans.find((plan) => plan.id === planId)

    if (plan) {
      return plan
    }
    return plans[0]
  }

  selectItemsPerOrder(itemsPerOrder: number) {
    this.setState({ itemsPerOrder })
  }

  calculateTotal() {
    const { numberOfPeople } = this.props
    const { itemsPerOrder } = this.state
    const tier = this.findPriceTier()
    return itemsPerOrder * numberOfPeople * tier.pricePerUnit
  }

  findPriceTier(): PricingTier {
    const { numberOfPeople } = this.props
    const { plan, itemsPerOrder } = this.state
    const servings = numberOfPeople * itemsPerOrder
    const planOption = plan.planOptions[0]
    return planOption.tiers.find((tier) => tier.min <= servings && tier.max > servings)
  }

  toggleOptions() {
    const { plan } = this.state
    const { planOptions } = plan
    const { itemsPerOrderOptions } = planOptions[0]
    return itemsPerOrderOptions.map((option) => ({ value: option, label: option }))
  }

  formatTotal() {
    const total = this.calculateTotal()
    return `$${total.toFixed(2)}`
  }

  ariaSubmitLabel() {
    const total = this.formatTotal()
    return `Select this plan for ${total}`
  }

  renderPerServing() {
    const pricingTier = this.findPriceTier()

    return (
      <div className="flex flex-col sm:flex-row text-center sm:text-left justify-between items-center py-3 px-4 bg-gray-100 border-solid border-t border-b border-gray-200">
        <div className="mb-2 sm:mb-0">
          <strong className="block">
            $
            {pricingTier.pricePerUnit}
            {" "}
            per serving
          </strong>
          1st week free shipping
        </div>
        <div className="text-right text-lg">
          <strong className="block">{this.formatTotal()}</strong>
          Total
        </div>
      </div>
    )
  }

  renderEmptyState() {
    return (
      <div className="text-center py-3 px-4 bg-gray-100 border-solid border-t border-b border-gray-200">
        Select an option below
      </div>
    )
  }

  renderPerWeek() {
    const { itemsPerOrder } = this.state
    const { numberOfPeople } = this.props

    return (
      <div className="flex flex-col sm:flex-row justify-between items-center py-3 px-4">
        <div className="mb-2 sm:mb-0">
          <strong className="block">Nights per week</strong>
          (
          {numberOfPeople}
          {" "}
          servings each)
        </div>
        <div className="pricing__toggle" role="listbox">
          <Toggle
            onSelect={this.selectItemsPerOrder}
            options={this.toggleOptions()}
            selected={itemsPerOrder}
          />
        </div>
      </div>
    )
  }

  renderCTA() {
    return (
      <div className="text-center mt-8">
        <a
          href={SIGNUP_URL}
          className="bg-secondary text-white py-3 px-8 text rounded-sm"
          aria-label={this.ariaSubmitLabel()}
        >
          Select This Plan
        </a>
      </div>
    )
  }

  render() {
    const { numberOfPeople } = this.props
    return (
      <div className="p-8 bg-white border border-gray-200 border-solid text-secondary text-lg w-full shadow">
        <h3 className="text-4xl font-bold">
          Dinner for
          {" "}
          {numberOfPeople}
        </h3>
        {this.renderPerWeek()}
        {this.renderPerServing()}
        {this.renderCTA()}
      </div>
    )
  }
}

export function PricingCalculator(props: PricingCalculatorProps) {
  return <PricingCalculatorInner {...props} />
}
